import { useCallback, useEffect, useRef, useState } from 'react';
import { getKanmonProducts } from 'api/financials';
import ProviderStore from 'store/Provider';
import KanmonStore from 'store/Kanmon';
import { TKanmonProduct } from 'types/financials';
import { getUserId } from 'utils/localstore';

const EMPTY_PRODUCTS: TKanmonProduct[] = [];
const useKanmonProducts = (): [TKanmonProduct[], boolean, () => Promise<void>, string | null] => {
    const [getProvider] = ProviderStore.useStore();
    const providerId = getProvider('data')?.id;
    const [getKanmon] = KanmonStore.useStore();
    const kanmon = getKanmon('snapshot');
    const revision = kanmon && kanmon.providerId === providerId ? kanmon.productsRevision : 0;
    const sequence = useRef(0);
    const pending = useRef<Promise<void>>();
    const [state, setState] = useState<{
        providerId?: number; revision: number; items: TKanmonProduct[]; loading: boolean; error: string | null;
    }>({ revision: -1, items: EMPTY_PRODUCTS, loading: false, error: null });

    const fetchData = useCallback(async () => {
        if (!providerId) return;
        if (pending.current) return pending.current;
        const version = ++sequence.current;
        const isCurrent = () => version === sequence.current && (ProviderStore.get('data')?.id ?? getUserId()) === providerId;
        setState({ providerId, revision, items: EMPTY_PRODUCTS, loading: true, error: null });
        const request = (async () => {
            try {
                const result = await getKanmonProducts();
                if (!isCurrent()) return;
                if (result.error || !Array.isArray(result.data?.data)) throw new Error('Unable to load financing products');
                setState({ providerId, revision, items: result.data.data, loading: false, error: null });
            } catch {
                if (isCurrent()) setState({ providerId, revision, items: EMPTY_PRODUCTS, loading: false, error: 'Unable to load financing products. Please retry.' });
            }
        })();
        pending.current = request;
        await request;
        if (pending.current === request) pending.current = undefined;
    }, [providerId, revision]);

    useEffect(() => {
        fetchData();
        return () => { sequence.current += 1; pending.current = undefined; };
    }, [fetchData]);

    if (state.providerId !== providerId || state.revision !== revision) return [EMPTY_PRODUCTS, !!providerId, fetchData, null];
    return [state.items, state.loading, fetchData, state.error];
};

export default useKanmonProducts;
